import {
  ConnectorInstance,
  DatapointInstance,
  DefaultDatapointTypes,
  EventDatapointParameters,
  ConnectorInstanceElement,
  ConnectableElement
} from '@olive/olive-types';
import * as EventEmitter from 'events';
import ConnectivityHolder, { ConnectivityDatapoint } from './ConnectivityHolder';
import AbstractDispatcher from './dispatchers/AbstractDispatcher';
import { isConnectorInstance, isDatapointInstance } from '@olive/types-util';
import { findConnectivityItem } from '../utils/ConnectivityFinder';

export default class ConnectivityManager {
  holder: ConnectivityHolder;
  eventManager: EventEmitter;
  dispatchers: { [key: string]: AbstractDispatcher };

  constructor(props: {
    holder: ConnectivityHolder;
    eventManager: EventEmitter;
  }) {
    this.holder = props.holder;
    this.eventManager = props.eventManager;
    this.dispatchers = {};
    this.setupEventListeners();
  }

  setupEventListeners() {
    const connectivitys = this.holder.getAll();
    for (const key in connectivitys) {
      const datapoint = connectivitys[key];
      if (isConnectorInstance(datapoint)) {
        this.setupConnectorInstanceEvents(datapoint, connectivitys);
      }
    }
  }

  private setupConnectorInstanceEvents(connectorInstance: ConnectorInstance, connectivitys: ReturnType<ConnectivityHolder['getAll']>) {
    // Handle events from elements
    if (connectorInstance.elements) {
      this.setupElementEvents(connectorInstance);
      // Handle events from source
      const source = connectorInstance.elements[0];
      if (!source) {
        return;
      }
      this.setupSourceEvents(source, connectorInstance, connectivitys);
    }
    else {
      console.warn('Connector instance missing elements', connectorInstance);
    }
  }

  private setupElementEvents(connectorInstance: ConnectorInstance) {
    const entries = connectorInstance.elements;
    for (let i = 0; i < entries.length; i++) {
      const resolvedConnectivity = findConnectivityItem(entries[i], this.holder);
      if (!resolvedConnectivity || !('type' in resolvedConnectivity)) {
        continue;
      }
      if (resolvedConnectivity.type === DefaultDatapointTypes.EVENT) {
        this.setupEventListener(
          entries[i] as DatapointInstance,
          resolvedConnectivity as DatapointInstance,
          connectorInstance
        );
      }
    }
  }

  private setupEventListener(
    sourceElement: DatapointInstance,
    resolvedConnectivity: DatapointInstance,
    connectorInstance: ConnectorInstance
  ) {
    const componentInstanceID = sourceElement.parameters?.componentInstanceID;
    const eventTopic = (resolvedConnectivity.datapoint as EventDatapointParameters).event.topic;
    const eventID = componentInstanceID ? componentInstanceID + '-' + eventTopic : eventTopic;

    console.info('Listening on event: ', eventID);
    this.eventManager.on(
      eventID,
      this.onEvent.bind(this, connectorInstance.instanceID || connectorInstance.instanceOf)
    );
  }

  private setupSourceEvents(
    source: ConnectorInstanceElement,
    connectorInstance: ConnectorInstance,
    connectivitys: ReturnType<ConnectivityHolder['getAll']>
  ) {
    // @ts-expect-error Source can be of type ConnectorTask which is not properly typed
    const sourceDatapointID = typeof source === 'string' ? source : source.instanceID || source.id || source.datapoint || source.instanceOf;
    const sourceDatapoint = connectivitys[sourceDatapointID] as ConnectivityDatapoint;

    if (!sourceDatapoint) {
      this.handleMissingSourceDatapoint(sourceDatapointID, connectorInstance);
      return;
    }

    this.setupSourceDatapointEvents(source, sourceDatapoint, connectorInstance);
  }

  private handleMissingSourceDatapoint(sourceDatapointID: string, connectorInstance: ConnectorInstance) {
    const strippedText: string = sourceDatapointID.replace(/^onAfter_(.*)/, '$1');
    const sourceDatapoint = this.holder.getAll()[strippedText] as ConnectivityDatapoint;

    if (!sourceDatapoint) {
      return;
    }

    this.eventManager.on(
      sourceDatapointID,
      this.onEvent.bind(this, connectorInstance.instanceID || connectorInstance.instanceOf)
    );
  }

  private setupSourceDatapointEvents(
    source: ConnectorInstanceElement,
    sourceDatapoint: ConnectivityDatapoint,
    connectorInstance: ConnectorInstance
  ) {
    if (sourceDatapoint?.datapoint?.type === DefaultDatapointTypes.EVENT) {
      const componentInstanceID = isDatapointInstance(source)
        ? (source as DatapointInstance).parameters?.componentInstanceID
        : null;

      const eventTopic = (sourceDatapoint.datapoint as EventDatapointParameters).event.topic;
      const eventID = componentInstanceID ? componentInstanceID + '-' + eventTopic : eventTopic;

      console.info('Listening on event: ', eventID);
      this.eventManager.on(
        eventID,
        this.onEvent.bind(this, connectorInstance.instanceID || connectorInstance.instanceOf)
      );
    }
  }

  async onEvent(
    connectorID: string,
    params?: unknown
  ) {
    let input: Record<string, unknown> = {};
    if (params instanceof FormData) {
      input.formData = params;
    } else if (typeof params === 'object' && params !== null) {
      input = { ...params as Record<string, unknown> };
    }
    const connector = this.holder.get(connectorID) as ConnectorInstance;
    const connectorInput = connector.parameters || {};

    input = { ...input, ...connectorInput };

    const result = await this.invoke({
      id: connectorID,
      input,
    });
    return result;
  }

  async invoke(props: { id: string, task?: ConnectableElement, input?: unknown }) {
    for (const key in this.dispatchers) {
      const dispatcher = this.dispatchers[key];
      if (dispatcher.canDispatch({ id: props.id })) {
        return await dispatcher.dispatch(props);
      }
    }
    console.error('No dispatcher found that can dispatch ' + props.id);
    throw 'No dispatcher found that can dispatch ' + props.id;
  }

  registerDispatcher(id: string, dispatcher: AbstractDispatcher) {
    if (this.dispatchers[id]) {
      console.warn('Dispatcher with id ' + id + ' already registered');
    }
    this.dispatchers[id] = dispatcher;
  }
}
